import React, { useEffect, useRef, useState } from 'react';

interface Props {
  /** Body text shown when the popover opens. */
  content: string;
  /** Glyph rendered inside the trigger button. Defaults to `?`. */
  glyph?: string;
  /**
   * Trigger size — the button sizes itself off this so it sits comfortably
   * next to a label in 11–14px ranges without growing the line height.
   */
  size?: number;
  /** Optional aria-label for the trigger. Defaults to "More info". */
  ariaLabel?: string;
}

const HelpPopover: React.FC<Props> = ({
  content,
  glyph = '?',
  size = 14,
  ariaLabel = 'More info',
}) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeydown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKeydown);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKeydown);
    };
  }, [open]);

  return (
    <span ref={wrapRef} style={wrap}>
      <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        style={{
          ...trigger,
          width: size,
          height: size,
          fontSize: Math.max(9, size - 4),
        }}
      >
        {glyph}
      </button>
      {open && (
        <div role="tooltip" style={panel}>
          {content}
          <span style={arrow} aria-hidden />
        </div>
      )}
    </span>
  );
};

const wrap: React.CSSProperties = {
  display: 'inline-flex',
  position: 'relative',
  alignItems: 'center',
  marginLeft: 4,
};

const trigger: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  border: '1px solid var(--border-2)',
  borderRadius: '50%',
  background: 'var(--bg-hover)',
  color: 'var(--fg-3)',
  fontWeight: 700,
  lineHeight: 1,
  cursor: 'pointer',
  fontFamily: 'var(--font-sans)',
};

const panel: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 8px)',
  left: '50%',
  transform: 'translateX(-50%)',
  minWidth: 200,
  maxWidth: 280,
  padding: '8px 10px',
  background: 'var(--fg-1)',
  color: '#fff',
  fontSize: 12,
  lineHeight: 1.45,
  fontWeight: 400,
  letterSpacing: 0,
  textTransform: 'none',
  borderRadius: 6,
  boxShadow: 'var(--shadow-lg)',
  zIndex: 1000,
  whiteSpace: 'normal',
  textAlign: 'left',
};

const arrow: React.CSSProperties = {
  position: 'absolute',
  top: -5,
  left: '50%',
  transform: 'translateX(-50%) rotate(45deg)',
  width: 10,
  height: 10,
  background: 'var(--fg-1)',
};

export default HelpPopover;
